import React from "react";

export const DEFAULT_RECEIPT_LINE_LENGTH = 42;
export const RECEIPT_BASE_FONT_SIZE_REM = 0.9;
export const RECEIPT_BASE_SMALL_FONT_SIZE_PX = 14;
export const RECEIPT_BASE_TOTAL_FONT_SIZE_PX = 24;
export const RECEIPT_CAPTURE_SCALE = 2;
export const RECEIPT_CAPTURE_PADDING_PX = 16;
export const RECEIPT_TYPE_TEXT = {
    order: "Xarid cheki",
    refund: "Qaytim cheki",
    credit: "Kredit cheki",
    prepaid: "Avans cheki",
};

export const ReceiptLineLengthContext = React.createContext(DEFAULT_RECEIPT_LINE_LENGTH);

export const getReceiptLineLength = (lineLength) => {
    const parsedLineLength = Number(lineLength);

    return Number.isFinite(parsedLineLength) && parsedLineLength > 0
        ? parsedLineLength
        : null;
};

export const getReceiptText = (value) => {
    if (value === null || value === undefined) return "";

    return String(value);
};

export const getReceiptLineText = (value) => getReceiptText(value).trim();

export const padReceiptLine = (value, maxLength, align = "left") => {
    const text = Array.from(getReceiptText(value)).slice(0, maxLength).join("");
    const spaceLength = maxLength - Array.from(text).length;

    if (align === "center" && spaceLength > 0) {
        const leftSpaceLength = Math.floor(spaceLength / 2);
        const rightSpaceLength = spaceLength - leftSpaceLength;

        return `${" ".repeat(leftSpaceLength)}${text}${" ".repeat(rightSpaceLength)}`;
    }

    return `${text}${" ".repeat(Math.max(spaceLength, 0))}`;
};

export const splitReceiptText = (value, maxLength, align = "left") => {
    const lines = [];
    const rawLines = getReceiptText(value).split(/\r\n|\r|\n/);

    rawLines.forEach((rawLine) => {
        const chars = Array.from(rawLine.trim());

        if (!chars.length) {
            lines.push(padReceiptLine("", maxLength, align));
            return;
        }

        for (let index = 0; index < chars.length; index += maxLength) {
            lines.push(padReceiptLine(chars.slice(index, index + maxLength).join("").trim(), maxLength, align));
        }
    });

    return lines.length ? lines : [padReceiptLine("", maxLength, align)];
};

export const formatReceiptLine = (leftValue, rightValue, maxLength) => {
    const left = getReceiptLineText(leftValue);
    const right = getReceiptLineText(rightValue);

    if (!right) return splitReceiptText(left, maxLength);

    const leftLength = Array.from(left).length;
    const rightLength = Array.from(right).length;
    const spaceLength = maxLength - leftLength - rightLength;

    if (spaceLength > 0) {
        return [`${left}${" ".repeat(spaceLength)}${right}`];
    }

    return [
        ...splitReceiptText(left, maxLength),
        ...splitReceiptText(right, maxLength),
    ];
};

export const normalizeReceiptLine = (line, maxLength) => {
    return padReceiptLine(line, maxLength);
};

export const renderReceiptChars = (line, maxLength) => (
    Array.from(normalizeReceiptLine(line, maxLength)).map((char, charIndex) => (
        <span className="receipt__char" key={`${char}-${charIndex}`}>
            {char}
        </span>
    ))
);

export const waitForReceiptImages = async (receiptElement) => {
    const images = Array.from(receiptElement.querySelectorAll("img"));

    await Promise.all(
        images.map((image) => {
            if (image.complete && image.naturalWidth > 0) return Promise.resolve();
            if (image.decode) return image.decode().catch(() => undefined);

            return new Promise((resolve) => {
                image.onload = resolve;
                image.onerror = resolve;
            });
        })
    );
};

export const getReceiptLogoCaptureStyles = (receiptElement) => {
    return Array.from(receiptElement.querySelectorAll(".receipt__company__logo")).map((image, index) => {
        const rect = image.getBoundingClientRect();

        return {
            index,
            width: rect.width,
            height: rect.height,
        };
    });
};

export const applyReceiptLogoCaptureStyles = (clonedReceiptElement, logoStyles) => {
    const clonedLogos = Array.from(clonedReceiptElement.querySelectorAll(".receipt__company__logo"));

    logoStyles.forEach(({index, width, height}) => {
        const clonedLogo = clonedLogos[index];
        if (!clonedLogo || !width || !height) return;

        clonedLogo.style.width = `${width}px`;
        clonedLogo.style.height = `${height}px`;
        clonedLogo.style.objectFit = "contain";
        clonedLogo.style.display = "block";
    });
};

export const addReceiptCanvasPadding = (canvas) => {
    const padding = RECEIPT_CAPTURE_PADDING_PX * RECEIPT_CAPTURE_SCALE;
    const paddedCanvas = document.createElement("canvas");
    paddedCanvas.width = canvas.width + padding * 2;
    paddedCanvas.height = canvas.height + padding * 2;

    const context = paddedCanvas.getContext("2d");
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, paddedCanvas.width, paddedCanvas.height);
    context.drawImage(canvas, padding, padding);

    return paddedCanvas;
};

export const ReceiptLine = ({left, right, text, maxLength, className = "", align = "left"}) => {
    const contextMaxLength = React.useContext(ReceiptLineLengthContext);
    const lineMaxLength = maxLength ?? contextMaxLength;
    const receiptLineClassName = className.split(" ").filter((name) => name !== "receipt__line--total").join(" ");

    const lines = text !== undefined
        ? splitReceiptText(text, lineMaxLength, align)
        : formatReceiptLine(left, right, lineMaxLength);

    return (
        <div className={`receipt__line ${receiptLineClassName}`}>
            {lines.map((line, index) => (
                <span className="receipt__line-text" key={`${line}-${index}`}>
                    {renderReceiptChars(line, lineMaxLength)}
                </span>
            ))}
        </div>
    );
};

export const ReceiptDivider = ({symbol = "-", className = "receipt__divider"}) => {
    const lineMaxLength = React.useContext(ReceiptLineLengthContext);

    return (
        <ReceiptLine
            className={`${className} receipt__divider-line`}
            text={symbol.repeat(lineMaxLength)}
        />
    );
};
